import React from 'react';
import CreateLayout from '@/shared/layout/createLayout';
import { TextField, Textarea } from '@/shared/components/TextField';
import { Snackbar } from '@/shared/components/Snackbar';
import { Loader } from '@/shared/components/Loader';
import { useCreateContactRequest } from '../../hooks/useCreateContactRequest';

const CreateContactRequest: React.FC = () => {
  const {
    subject,
    description,
    errors,
    isSubmitting,
    submitError,
    isCancelModalOpen,
    profile,
    isProfileLoading,
    handleSubjectChange,
    handleDescriptionChange,
    handleSubmit,
    handleCancel,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleCloseErrorSnackbar,
    SUBJECT_MAX,
    DESCRIPTION_MAX,
  } = useCreateContactRequest();

  const Header = () => (
    <p className="text-titleLarge text-neutral-10">
      Utwórz zgłoszenie kontaktowe
    </p>
  );

  const address = profile?.address;

  if (isProfileLoading) {
    return (
      <div className="flex items-center justify-center w-full h-full">
        <Loader text="Ładowanie danych..." />
      </div>
    );
  }

  return (
    <>
      <CreateLayout
        isSubmitting={isSubmitting}
        handleSubmit={handleSubmit}
        handleCancel={handleCancel}
        isCancelModalOpen={isCancelModalOpen}
        handleCloseCancelModal={handleCloseCancelModal}
        handleConfirmCancel={handleConfirmCancel}
        isSnackbarOpen={false}
        handleCloseSnackbar={handleCloseErrorSnackbar}
        snackbarError=""
        Header={Header}
        submitButtonLabel="Wyślij"
        submitButtonLoadingLabel="Wysyłanie..."
        cancelButtonLabel="Anuluj"
        submitButtonDisabled={isSubmitting || isProfileLoading}
      >
        {/* Section 1: Request details */}
        <div className="flex flex-col gap-2">
          <p className="text-[18px] font-normal text-on-surface-variant">
            Szczegóły zgłoszenia
          </p>
          <div className="flex flex-col gap-8 w-[66%]">
            <TextField
              label="Temat"
              type="text"
              fullWidth
              maxLength={SUBJECT_MAX}
              value={subject}
              onChange={handleSubjectChange}
              error={Boolean(errors.subject)}
              errorMessage={errors.subject ?? ''}
              disabled={isSubmitting}
              autoFocus
            />
            <Textarea
              label="Opis"
              fullWidth
              rows={4}
              maxLength={DESCRIPTION_MAX}
              value={description}
              onChange={handleDescriptionChange}
              error={Boolean(errors.description)}
              errorMessage={errors.description ?? ''}
              disabled={isSubmitting}
            />
          </div>
        </div>

        {/* Section 2: Basic client data (read-only) */}
        <div className="flex flex-col gap-2 border-y-1 border-y-[#DCDDE0] py-8">
          <p className="text-[18px] font-normal text-on-surface-variant">
            Podstawowe dane
          </p>
          <div className="flex flex-col gap-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <TextField
                label="ID klienta"
                value={profile?.clientId ?? ''}
                fullWidth
                disabled={true}
              />
              <TextField
                label="Imię i nazwisko"
                value={profile?.name ?? ''}
                fullWidth
                disabled={true}
              />
              <TextField
                label="Adres e-mail"
                value={profile?.email ?? ''}
                fullWidth
                disabled={true}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <TextField
                label="Numer telefonu"
                type="tel"
                value={profile?.phoneNumber ?? ''}
                fullWidth
                disabled={true}
              />
            </div>
          </div>
        </div>

        {/* Section 3: Address data (read-only) */}
        <div className="flex flex-col gap-2">
          <p className="text-[18px] font-normal text-on-surface-variant">
            Dane adresowe
          </p>
          <div className="flex flex-col gap-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <TextField
                label="Adres (linia 1)"
                value={address?.addressLine1 ?? ''}
                fullWidth
                disabled={true}
              />
              <TextField
                label={
                  <>
                    Adres (linia 2){' '}
                    <span style={{ fontSize: 12 }}>(Opcjonalne)</span>
                  </>
                }
                value={address?.addressLine2 ?? ''}
                fullWidth
                disabled={true}
              />
              <TextField
                label="Miejscowość"
                value={address?.city ?? ''}
                fullWidth
                disabled={true}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <TextField
                label="Województwo"
                value={address?.state ?? ''}
                fullWidth
                disabled={true}
              />
              <TextField
                label={
                  <>
                    Kod pocztowy{' '}
                    <span style={{ fontSize: 12 }}>(Opcjonalne)</span>
                  </>
                }
                value={address?.zipCode ?? ''}
                fullWidth
                disabled={true}
              />
              <TextField
                label="Kraj"
                value={address?.country ?? ''}
                fullWidth
                disabled={true}
              />
            </div>
          </div>
        </div>
      </CreateLayout>

      {/* Error snackbar */}
      {submitError && (
        <Snackbar
          open={Boolean(submitError)}
          message={submitError}
          variant="error"
          onClose={handleCloseErrorSnackbar}
          autoHideDuration={5000}
          position="top-center"
          showCloseButton
        />
      )}
    </>
  );
};

export default CreateContactRequest;
